import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { VStack, Flex, Text, Box } from "@sqymagma/elements";

import { MoodCategory, MoodPayload, MoodState } from "@types";
import { Button, Editor, Icon, Tooltip } from "@elements";
import { IconNames } from "@elements/Icon";
import { WidgetBox } from "@components";
import { remoteApi } from "@services";
import { darkenHexColor, getRandom } from "@helpers";
import { useStyle, useToast } from "@contexts";
import { THEMES } from "@constants";

import { successMsg } from "./data";
import * as S from "./style";

interface MoodWidgetProps {
  onSubmit?: () => void;
  update?: MoodState;
  modalStyle?: boolean;
}

const moodsIcons = [
  "reallyBadMood",
  "badMood",
  "neutralMood",
  "happyMood",
  "reallyHappyMood",
] as const;

const MoodWidget = (props: MoodWidgetProps) => {
  const { onSubmit, update, modalStyle } = props;

  const { addToast } = useToast();

  const { theme } = useStyle();
  const isDarkTheme = theme === THEMES.dark;

  const initialState = useMemo(
    () => ({
      mood: update?.mood || null,
      hidden: update?.hidden || false,
      message: update?.message || "",
      category: update?.category || null,
    }),
    [update]
  );
  const [state, setState] = useState<MoodPayload>(initialState);
  const [isSending, setIsSending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const history = useHistory();
  const [openMoodsMenu, setOpenMoodsMenu] = useState<boolean>(false);

  useEffect(() => {
    if (isSuccess && state.category !== "moods") {
      setState(initialState);
    }
  }, [initialState, isSuccess, state.category]);

  const handleAction = () => {
    history.push("/mood");
  };

  const handleEditorChange = (content: string) =>
    setState((state) => ({ ...state, message: content }));

  const handleCreate = async () => {
    setIsSending(true);
    const createOk = await remoteApi.createMood(state);
    if (createOk) {
      setIsSuccess(true);
    }
    setIsSending(false);
    onSubmit && onSubmit();
  };

  const handleUpdate = async () => {
    setIsSending(true);
    const updateOk = update && (await remoteApi.updateMood(update.id, state));
    if (updateOk) {
      addToast("Mensaje actualizado");
    } else {
      addToast("⚠️ ¡Oops! Error al actualizar el mensaje");
    }
    setIsSending(false);
    onSubmit && onSubmit();
  };

  const handleMoodClick = (mood: number) => () => {
    const text = getRandom(successMsg[mood - 1].text);
    setState((state) => ({ ...state, category: "moods", mood }));
    setMessage(text);
    setOpenMoodsMenu(false);
  };

  const handleCategoryClick = (category: MoodCategory) => () => {
    if (category === "moods") {
      setOpenMoodsMenu((open) => !open);
    } else {
      setOpenMoodsMenu(false);
      setState((state) => ({ ...state, category, mood: null }));
    }
  };

  const MoodIcon = useCallback(
    (props: {
      icon: IconNames;
      active: boolean;
      onClick: () => void;
      size?: number;
    }) => {
      const { icon, active, onClick, size = 50 } = props;
      const iconName = active ? (`${icon}On` as IconNames) : icon;
      return (
        <S.IconWrapper onClick={onClick}>
          <Icon name={iconName} width={size} height={size} />
        </S.IconWrapper>
      );
    },
    []
  );

  const allowSend =
    ((state.category && state.category !== "moods") || state.mood) &&
    !isSending;

  const placeholder =
    "Un libro, un plan, un logro o el último virtual coffee con tus compañeros...";

  const successMsgColor =
    state.mood &&
    (isDarkTheme
      ? darkenHexColor(successMsg[state.mood - 1].color, 0.7)
      : successMsg[state.mood - 1].color);

  return (
    <WidgetBox $shadow={update || modalStyle ? "none" : "normal"}>
      <VStack>
        {!update && (
          <Flex justifyContent="space-between" alignItems="center" mb="xs">
            <Text textStyle="subtitle01" color="text01" weight="bold">
              ¿Tienes algo que compartir?
            </Text>
          </Flex>
        )}

        {isSuccess && state.mood ? (
          <>
            <S.SuccessBox bg={successMsgColor} p="xs" mt="xxs">
              <Text textStyle="body" color="text01">
                {message}
              </Text>
            </S.SuccessBox>
            <Flex justifyContent="flex-end" mt="xs">
              <Button
                rightIcon="arrowRight2"
                action={handleAction}
                animateHover
              >
                Ver pulso del equipo
              </Button>
            </Flex>
          </>
        ) : (
          <>
            <Flex justifyContent="space-between" mb="xs">
              <Tooltip hideOnClick content="Recomendaciones">
                <MoodIcon
                  icon="recommendationsMood"
                  active={state.category === "recommendations"}
                  onClick={handleCategoryClick("recommendations")}
                />
              </Tooltip>
              <Tooltip hideOnClick content="Recursos">
                <MoodIcon
                  icon="resourcesMood"
                  active={state.category === "resources"}
                  onClick={handleCategoryClick("resources")}
                />
              </Tooltip>
              <Tooltip hideOnClick content="About SQY">
                <MoodIcon
                  icon="secuoyasMood"
                  active={state.category === "secuoyas"}
                  onClick={handleCategoryClick("secuoyas")}
                />
              </Tooltip>
              <S.MoodSubmenuAnchor>
                {openMoodsMenu && (
                  <S.MoodSubmenu>
                    <MoodIcon
                      icon="reallyBadMood"
                      active={state.mood === 1}
                      onClick={handleMoodClick(1)}
                      size={32}
                    />
                    <MoodIcon
                      icon="badMood"
                      active={state.mood === 2}
                      onClick={handleMoodClick(2)}
                      size={32}
                    />
                    <MoodIcon
                      icon="neutralMood"
                      active={state.mood === 3}
                      onClick={handleMoodClick(3)}
                      size={32}
                    />
                    <MoodIcon
                      icon="happyMood"
                      active={state.mood === 4}
                      onClick={handleMoodClick(4)}
                      size={32}
                    />
                    <MoodIcon
                      icon="reallyHappyMood"
                      active={state.mood === 5}
                      onClick={handleMoodClick(5)}
                      size={32}
                    />
                  </S.MoodSubmenu>
                )}
                <Tooltip hideOnClick content="Estados de ánimo">
                  <MoodIcon
                    icon={state.mood ? moodsIcons[state.mood - 1] : "moodsMood"}
                    active={state.category === "moods" && state.mood !== null}
                    onClick={handleCategoryClick("moods")}
                  />
                </Tooltip>
              </S.MoodSubmenuAnchor>
              <Tooltip hideOnClick content="Virtual Coffees">
                <MoodIcon
                  icon="coffeesMood"
                  active={state.category === "coffees"}
                  onClick={handleCategoryClick("coffees")}
                />
              </Tooltip>
            </Flex>

            <Box>
              <Editor
                value={state.message}
                onChange={handleEditorChange}
                placeholder={placeholder}
                buttons={["youtube", "spotify"]}
                height={modalStyle ? 150 : undefined}
              />
            </Box>

            <Flex justifyContent="flex-end" mt="xs">
              <Button
                rightIcon="arrowRight2"
                disabled={!allowSend}
                action={update ? handleUpdate : handleCreate}
                animateHover
              >
                {update ? "Actualizar" : "Enviar"}
              </Button>
            </Flex>
          </>
        )}
      </VStack>
    </WidgetBox>
  );
};

export default MoodWidget;
